// ProductCatalogPicker — shared product-selection UI used across surfaces
// that need the user to pick a subset of their saved products:
//
//   - Ad Pilot (`/schedule/auto`) — multi-select, NO All-Products toggle.
//     User must explicitly pick what to schedule.
//   - Ad Strategist Step 4 — multi-select, WITH All-Products toggle.
//     Default "All Products" → uses the entire catalog. Toggle off →
//     same tab-based picker as Ad Pilot.
//
// State is self-contained — the parent only sees the SELECTION via
// onSelectionChange (id list + optional collection_id + all-products flag).
// To reset the picker externally, give it a new `key` prop and React will
// remount it. This keeps the prop surface small while still letting the
// parent observe everything it needs for submit.
//
// Loads products + collections itself on mount; reuses the existing
// listProducts / listCollections / listCollectionProducts endpoints.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listCollectionProducts,
  listCollections,
  listProducts,
  type Collection,
  type UserProduct,
} from '@/lib/api';

export interface ProductPickerSelection {
  /** Union of (resolved collection members) + (manual picks). De-duped.
   *  Empty when allSelected=true — caller should treat allSelected as the
   *  authoritative source in that case. */
  selectedIds: string[];
  /** Single collection FK for backend's `collection_id` metadata field.
   *  Only set when EXACTLY one collection is checked AND no manual picks.
   *  Null otherwise (the selectedIds union still ships either way). */
  collectionId: string | null;
  /** Set when the picker's "All Products" toggle is ON. Caller can use
   *  this to short-circuit selectedIds (since "all" is conceptually
   *  different from "manual picks happen to equal the catalog"). Only
   *  ever true when includeAllToggle=true. */
  allSelected: boolean;
}

type PickerTab = 'collection' | 'products';

export interface ProductCatalogPickerProps {
  /** Show the "All Products" toggle row at the top. Default true.
   *  Set false to force the user into manual picking (Ad Pilot's mode). */
  includeAllToggle?: boolean;
  /** Show the Collections tab. Default true. Set false to remove the
   *  tabs entirely + go straight to the Pick Products grid. */
  includeCollections?: boolean;
  /** When includeAllToggle=true, this is the initial value of the toggle.
   *  Default true (start with All Products selected). */
  initialAllSelected?: boolean;
  /** Seed selection for manual mode. Used when re-entering the picker
   *  with a prior selection (e.g. user clicked Edit on Step 4 after
   *  reaching Step 5). Defaults to empty. */
  initialSelectedIds?: string[];
  /** Fires on every selection change. Parent stashes the latest value
   *  + uses it on submit. */
  onSelectionChange: (next: ProductPickerSelection) => void;
}

export default function ProductCatalogPicker({
  includeAllToggle = true,
  includeCollections = true,
  initialAllSelected = true,
  initialSelectedIds = [],
  onSelectionChange,
}: ProductCatalogPickerProps) {
  const navigate = useNavigate();

  // -------- Catalog data --------
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  // -------- Picker state --------
  // Initial tab: Collection if the user has any collection with products,
  // else Products. Matches Ad Pilot's heuristic so the picker lands users
  // on the most-useful tab without an extra click.
  const [pickerTab, setPickerTab] = useState<PickerTab>('products');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [focusedCollectionId, setFocusedCollectionId] = useState<string | null>(null);
  const [collectionProductsCache, setCollectionProductsCache] = useState<
    Record<string, UserProduct[]>
  >({});
  const [collectionLoadingId, setCollectionLoadingId] = useState<string | null>(null);
  const [manualProductIds, setManualProductIds] = useState<string[]>(initialSelectedIds);
  const [productSearch, setProductSearch] = useState('');

  // Jul 26 — HARD CAP at 4 products. With a large catalog the batch concept
  // writer stitches copy across products (a "routine" headline mashing several
  // items onto one product), so we limit every picker to ≤4 and disable the
  // "All Products" path (which would send the whole catalog). ALLOW_ALL_PRODUCTS
  // stays false everywhere; flip it back once the concept writer is per-product.
  const MAX_PRODUCTS = 4;
  const ALLOW_ALL_PRODUCTS = false;

  // "All Products" toggle — disabled by the cap (ALLOW_ALL_PRODUCTS=false), so
  // allSelected can never become true. Kept as state to avoid churn elsewhere.
  const [allSelected, setAllSelected] = useState<boolean>(false);

  // -------- Initial load --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prods, cols] = await Promise.all([
          listProducts().catch(() => [] as UserProduct[]),
          includeCollections
            ? listCollections().catch(() => [] as Collection[])
            : Promise.resolve([] as Collection[]),
        ]);
        if (cancelled) return;
        setProducts(prods);
        setCollections(cols);

        // Lazy-warm the first non-empty collection so the user gets an
        // instant preview when they click the Collection tab. Doesn't
        // auto-focus or auto-select (per Ad Pilot UX lock).
        const firstWithProducts = cols.find((c) => c.productCount > 0);
        if (firstWithProducts) {
          setFocusedCollectionId(firstWithProducts.id);
          listCollectionProducts(firstWithProducts.id)
            .then((cps) => {
              if (cancelled) return;
              setCollectionProductsCache((prev) => ({
                ...prev,
                [firstWithProducts.id]: cps,
              }));
            })
            .catch(() => { /* user can retry */ });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeCollections]);

  // Jul 26 — Real-time reclassify refresh. Products imported in bulk land
  // unclassified (grayed "Classifying…"); the background backfill fills in
  // their category over the next few seconds. Poll listProducts while ANY
  // product is still unclassified so tiles un-gray live, without the user
  // closing + reopening the picker. Stops the instant everything's classified;
  // a ~3-min safety cap prevents an open modal from polling forever if a
  // product genuinely can't be classified (the cron finishes those).
  const hasUnclassified = useMemo(
    () => products.some((p) => !p.productCategory),
    [products],
  );
  useEffect(() => {
    if (!hasUnclassified) return;
    let cancelled = false;
    let ticks = 0;
    const MAX_TICKS = 45; // ~3 min at 4s
    const iv = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) { clearInterval(iv); return; }
      try {
        const fresh = await listProducts();
        if (!cancelled) setProducts(fresh);
      } catch { /* transient — retry next tick */ }
    }, 4000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [hasUnclassified]);

  // -------- Derived: the union of collection members + manual picks --------
  const selectedIds = useMemo<string[]>(() => {
    if (allSelected) return [];   // caller treats allSelected as authoritative
    const out = new Set<string>();
    for (const cid of selectedCollectionIds) {
      const prods = collectionProductsCache[cid] ?? [];
      for (const p of prods) out.add(p.id);
    }
    for (const pid of manualProductIds) out.add(pid);
    return Array.from(out);
  }, [allSelected, selectedCollectionIds, collectionProductsCache, manualProductIds]);

  const collectionId = useMemo<string | null>(() => {
    if (allSelected) return null;
    if (selectedCollectionIds.length === 1 && manualProductIds.length === 0) {
      return selectedCollectionIds[0];
    }
    return null;
  }, [allSelected, selectedCollectionIds, manualProductIds]);

  // Effective selection is capped at MAX_PRODUCTS (see comment near the top).
  const atCap = selectedIds.length >= MAX_PRODUCTS;

  // Bubble every change up to the parent. Memo on a tuple so we don't fire
  // a redundant callback when the values are unchanged.
  useEffect(() => {
    onSelectionChange({ selectedIds, collectionId, allSelected });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(','), collectionId, allSelected]);

  // -------- Filtering --------
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const haystack = [p.name, p.description, p.productCategory]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [products, productSearch]);

  const focusedCollectionProducts = useMemo<UserProduct[] | null>(() => {
    if (!focusedCollectionId) return null;
    return collectionProductsCache[focusedCollectionId] ?? null;
  }, [focusedCollectionId, collectionProductsCache]);

  // -------- Handlers --------
  const ensureCollectionLoaded = useCallback(
    async (id: string): Promise<UserProduct[] | null> => {
      if (collectionProductsCache[id]) return collectionProductsCache[id];
      setCollectionLoadingId(id);
      try {
        const cps = await listCollectionProducts(id);
        setCollectionProductsCache((prev) => ({ ...prev, [id]: cps }));
        return cps;
      } catch {
        return null;
      } finally {
        setCollectionLoadingId(null);
      }
    },
    [collectionProductsCache],
  );

  const focusCollection = useCallback(
    async (id: string) => {
      setFocusedCollectionId(id);
      await ensureCollectionLoaded(id);
    },
    [ensureCollectionLoaded],
  );

  const toggleCollection = useCallback(
    async (id: string) => {
      const isChecked = selectedCollectionIds.includes(id);
      if (isChecked) {
        setSelectedCollectionIds((prev) => prev.filter((c) => c !== id));
      } else {
        const loaded = await ensureCollectionLoaded(id);
        if (loaded === null) return;
        const colIds = new Set(loaded.map((p) => p.id));
        // Enforce the 4-product cap: count how many NEW products this
        // collection would add on top of the current effective selection.
        const currentCovered = new Set(selectedIds);
        const addedByCollection = [...colIds].filter((pid) => !currentCovered.has(pid));
        if (currentCovered.size + addedByCollection.length > MAX_PRODUCTS) {
          window.alert(`You can pick up to ${MAX_PRODUCTS} products. This collection would exceed that — remove some first or pick products individually.`);
          return;
        }
        setManualProductIds((prev) => prev.filter((pid) => !colIds.has(pid)));
        setSelectedCollectionIds((prev) =>
          prev.includes(id) ? prev : [...prev, id],
        );
      }
    },
    [selectedCollectionIds, selectedIds, ensureCollectionLoaded],
  );

  /** Manual single-product toggle. Ignored when the product is covered
   *  by a checked collection — uncheck the collection to remove it. */
  const toggleProduct = useCallback(
    (id: string) => {
      for (const cid of selectedCollectionIds) {
        const inCol = (collectionProductsCache[cid] ?? []).some((p) => p.id === id);
        if (inCol) return;
      }
      setManualProductIds((prev) => {
        if (prev.includes(id)) return prev.filter((p) => p !== id);
        // Adding — block if the effective selection is already at the cap.
        const covered = new Set<string>();
        for (const cid of selectedCollectionIds) {
          for (const p of collectionProductsCache[cid] ?? []) covered.add(p.id);
        }
        for (const pid of prev) covered.add(pid);
        if (covered.size >= MAX_PRODUCTS) return prev;
        return [...prev, id];
      });
    },
    [selectedCollectionIds, collectionProductsCache],
  );

  // -------- Render --------
  if (loading) {
    return (
      <div className="rounded-2xl border-2 border-surface-dark bg-white p-8 text-center min-h-[320px] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-dark-muted/60" />
      </div>
    );
  }

  // No products at all — same empty state regardless of mode.
  if (products.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-surface-dark bg-white p-8 text-center min-h-[320px] flex flex-col items-center justify-center">
        <i className="ri-shopping-bag-3-line text-3xl text-dark-muted/40 mb-2" />
        <p className="text-sm text-dark font-semibold">No products in your library</p>
        <p className="text-xs text-dark-muted mt-1 mb-3">
          Add some products to your catalog first.
        </p>
        <button
          type="button"
          onClick={() => navigate('/products')}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer"
        >
          Open My Products
        </button>
      </div>
    );
  }

  const productsCovered = selectedIds.length;
  const summaryCount = allSelected ? products.length : productsCovered;

  return (
    <div className="space-y-3">
      {/* ====== Optional "All Products" toggle row (disabled by the cap) ====== */}
      {includeAllToggle && ALLOW_ALL_PRODUCTS && (
        <div className={`
          rounded-2xl border-2 px-4 py-3 flex items-center gap-3 transition-all
          ${allSelected
            ? 'bg-primary/5 border-primary'
            : 'bg-white border-surface-dark/40'}
        `}>
          <button
            type="button"
            onClick={() => setAllSelected((v) => !v)}
            className={`
              flex-shrink-0 w-11 h-6 rounded-full relative transition-colors cursor-pointer
              ${allSelected ? 'bg-primary' : 'bg-surface-dark/40'}
            `}
            aria-label={allSelected ? 'Disable All Products' : 'Enable All Products'}
            aria-pressed={allSelected}
          >
            <div className={`
              absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all
              ${allSelected ? 'left-5' : 'left-0.5'}
            `} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-dark">All Products</div>
            <p className="text-xs text-dark-muted leading-tight">
              {allSelected
                ? `Using your full catalog — ${products.length} product${products.length === 1 ? '' : 's'}.`
                : 'Pick a custom subset below.'}
            </p>
          </div>
        </div>
      )}

      {/* ====== Customize section (hidden when All is on) ====== */}
      {!allSelected && (
        <>
          {/* Selection count chip */}
          <div className="flex items-center justify-between gap-2 px-1">
            <div className={`text-xs ${atCap ? 'text-primary font-bold' : 'text-dark-muted'}`}>
              {productsCovered === 0
                ? `Pick up to ${MAX_PRODUCTS} products.`
                : `${productsCovered} of ${MAX_PRODUCTS} product${MAX_PRODUCTS === 1 ? '' : 's'} selected${atCap ? ' — max reached' : ''}`}
            </div>
            {(selectedCollectionIds.length > 0 || manualProductIds.length > 0) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCollectionIds([]);
                  setManualProductIds([]);
                }}
                className="text-xs font-bold text-dark-muted hover:text-primary cursor-pointer"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Tabs (only when collections enabled) */}
          {includeCollections && (
            <div className="flex p-1 rounded-full bg-surface border border-surface-dark/40 max-w-md">
              <button
                type="button"
                onClick={() => setPickerTab('products')}
                className={`
                  flex-1 px-4 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer
                  ${pickerTab === 'products'
                    ? 'bg-white text-primary shadow-md shadow-primary/15'
                    : 'text-dark-muted hover:text-dark'}
                `}
              >
                Pick products
              </button>
              <button
                type="button"
                onClick={() => setPickerTab('collection')}
                className={`
                  flex-1 px-4 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer
                  ${pickerTab === 'collection'
                    ? 'bg-white text-primary shadow-md shadow-primary/15'
                    : 'text-dark-muted hover:text-dark'}
                `}
              >
                Use a collection
              </button>
            </div>
          )}

          {/* ============ PICK PRODUCTS TAB ============ */}
          {(!includeCollections || pickerTab === 'products') && (
            <div className="rounded-2xl border-2 border-surface-dark bg-white p-4 min-h-[440px] flex flex-col">
              {/* Search */}
              <div className="relative mb-3">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted/60 text-base pointer-events-none" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search by name, description, or category…"
                  className="w-full pl-9 pr-9 py-2 rounded-full bg-surface/50 border border-surface-dark/40 text-sm placeholder:text-dark-muted/60 focus:outline-none focus:border-primary focus:bg-white transition-all"
                />
                {productSearch && (
                  <button
                    type="button"
                    onClick={() => setProductSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-surface-dark/20 flex items-center justify-center cursor-pointer text-dark-muted"
                    aria-label="Clear search"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                )}
              </div>

              {/* Grid */}
              {filteredProducts.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-xs text-dark-muted">
                  No products match "{productSearch}".
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-1">
                  {filteredProducts.map((p) => {
                    const isManual = manualProductIds.includes(p.id);
                    const isFromCollection = !isManual && selectedIds.includes(p.id);
                    const isSelected = isManual || isFromCollection;
                    // Jul 26 — an unclassified product (category null/empty, but
                    // NOT 'none' which is a real "no recipe" classification) has
                    // no type anchor, so it poisons batch copy + recipe picking.
                    // Gray it out + block selection until classification lands.
                    const isUnclassified = !p.productCategory;
                    const capBlocked = !isSelected && !isUnclassified && atCap;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleProduct(p.id)}
                        disabled={isFromCollection || capBlocked || isUnclassified}
                        title={
                          isFromCollection
                            ? 'From a checked collection — uncheck the collection to remove'
                            : isUnclassified
                              ? "Still importing — we're classifying this product. It'll be ready to use in a moment."
                              : capBlocked
                                ? `Up to ${MAX_PRODUCTS} products — remove one to add another`
                                : p.name || ''
                        }
                        className={`
                          flex flex-col gap-1 text-left p-0 bg-transparent cursor-pointer
                          ${isFromCollection ? 'cursor-not-allowed' : ''}
                          ${capBlocked ? 'cursor-not-allowed opacity-40' : ''}
                          ${isUnclassified ? 'cursor-not-allowed' : ''}
                        `}
                      >
                        <div className={`
                          relative aspect-square rounded-xl overflow-hidden
                          border-2 transition-all bg-surface/40
                          ${isSelected
                            ? 'border-primary shadow-md shadow-primary/25 ring-2 ring-primary/30'
                            : 'border-surface-dark/30 hover:border-primary/40'}
                        `}>
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name || ''}
                              className={`absolute inset-0 w-full h-full object-contain p-1.5 ${isUnclassified ? 'grayscale opacity-50' : ''}`}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-dark-muted/40">
                              <i className="ri-image-line text-2xl" />
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                              <i className={`text-xs ${isFromCollection ? 'ri-stack-line' : 'ri-check-line'}`} />
                            </div>
                          )}
                          {isUnclassified && (
                            <div className="absolute inset-x-0 bottom-0 bg-dark/70 text-white text-[10px] font-bold px-1 py-0.5 flex items-center justify-center gap-1">
                              <i className="ri-loader-4-line animate-spin text-[11px]" />
                              Classifying…
                            </div>
                          )}
                        </div>
                        <span className={`text-xs leading-tight line-clamp-2 ${
                          isSelected ? 'text-primary font-bold' : 'text-dark-muted'
                        }`}>
                          {p.name || 'Untitled'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ============ COLLECTIONS TAB ============ */}
          {includeCollections && pickerTab === 'collection' && (
            collections.length === 0 ? (
              <div className="rounded-2xl border-2 border-surface-dark bg-white p-8 text-center min-h-[440px] flex flex-col items-center justify-center">
                <i className="ri-stack-line text-3xl text-dark-muted/40 mb-2" />
                <p className="text-sm text-dark font-semibold">No collections yet</p>
                <p className="text-xs text-dark-muted mt-1 mb-3">
                  Switch back to Pick Products, or build a collection in My Products.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/products')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer"
                >
                  Open My Products
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] gap-3 min-h-[440px]">
                {/* Left: collection list */}
                <div className="rounded-2xl border-2 border-surface-dark bg-white p-3 flex flex-col">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-dark-muted/70 px-2 mb-2">
                    Collections ({collections.length})
                  </div>
                  <div className="flex-1 overflow-y-auto pr-1 space-y-1 max-h-[400px]">
                    {collections.map((c) => {
                      const checked = selectedCollectionIds.includes(c.id);
                      const focused = focusedCollectionId === c.id;
                      const loading = collectionLoadingId === c.id;
                      const disabled = c.productCount === 0;
                      return (
                        <div
                          key={c.id}
                          onClick={() => !disabled && focusCollection(c.id)}
                          role="button"
                          tabIndex={disabled ? -1 : 0}
                          className={`
                            group relative flex items-center gap-2.5 p-2.5 rounded-xl
                            border-2 transition-all
                            ${disabled
                              ? 'border-surface-dark/20 opacity-50 cursor-not-allowed'
                              : focused
                                ? 'border-primary bg-primary/5 cursor-pointer'
                                : 'border-surface-dark/30 bg-white hover:border-primary/40 cursor-pointer'}
                          `}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!disabled) void toggleCollection(c.id);
                            }}
                            disabled={disabled || loading}
                            className={`
                              w-5 h-5 rounded-md border-2 flex-shrink-0
                              flex items-center justify-center transition-all
                              ${checked
                                ? 'bg-primary border-primary text-white'
                                : 'bg-white border-surface-dark/40 hover:border-primary/50'}
                              ${(disabled || loading) ? 'cursor-not-allowed' : 'cursor-pointer'}
                            `}
                            aria-label={checked ? 'Uncheck' : 'Check'}
                          >
                            {loading ? (
                              <i className="ri-loader-4-line animate-spin text-xs" />
                            ) : checked ? (
                              <i className="ri-check-line text-xs" />
                            ) : null}
                          </button>
                          {/* 2×2 thumb mosaic */}
                          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden grid grid-cols-2 grid-rows-2 gap-px bg-surface-dark/20">
                            {[0, 1, 2, 3].map((i) => (
                              <div key={i} className="bg-surface relative">
                                {c.thumbnails[i] ? (
                                  <img
                                    src={c.thumbnails[i]}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : null}
                              </div>
                            ))}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-dark text-sm truncate">{c.name}</div>
                            <div className="text-[11px] text-dark-muted">
                              {c.productCount} product{c.productCount === 1 ? '' : 's'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: focused collection preview */}
                <div className="rounded-2xl border-2 border-surface-dark bg-white p-4 flex flex-col">
                  {focusedCollectionId && focusedCollectionProducts ? (
                    <>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div>
                          <h3 className="font-display font-bold text-dark text-sm leading-tight">
                            {collections.find((c) => c.id === focusedCollectionId)?.name ?? 'Collection'}
                          </h3>
                          <p className="text-[11px] text-dark-muted leading-tight mt-0.5">
                            {focusedCollectionProducts.length} product{focusedCollectionProducts.length === 1 ? '' : 's'} — check the collection to add them all.
                          </p>
                        </div>
                        {selectedCollectionIds.includes(focusedCollectionId) ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-primary px-2 py-1 rounded-full bg-primary/10">
                            <i className="ri-check-line" /> Added
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void toggleCollection(focusedCollectionId)}
                            disabled={focusedCollectionProducts.length === 0}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:text-primary-hover px-2.5 py-1 rounded-full bg-primary/5 border border-primary/30 hover:bg-primary/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <i className="ri-add-line" /> Add collection
                          </button>
                        )}
                      </div>
                      {focusedCollectionProducts.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center text-xs text-dark-muted">
                          This collection is empty.
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[400px] overflow-y-auto pr-1">
                          {focusedCollectionProducts.map((p) => {
                            const isSelected = selectedIds.includes(p.id);
                            return (
                              <div key={p.id} className="flex flex-col gap-1">
                                <div className={`
                                  relative aspect-square rounded-xl overflow-hidden
                                  border-2 transition-all bg-surface/40
                                  ${isSelected
                                    ? 'border-primary shadow-md shadow-primary/25 ring-2 ring-primary/30'
                                    : 'border-surface-dark/30'}
                                `}>
                                  {p.imageUrl ? (
                                    <img
                                      src={p.imageUrl}
                                      alt={p.name || ''}
                                      className="absolute inset-0 w-full h-full object-contain p-1.5"
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-dark-muted/40">
                                      <i className="ri-image-line text-2xl" />
                                    </div>
                                  )}
                                  {isSelected && (
                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                                      <i className="ri-check-line text-xs" />
                                    </div>
                                  )}
                                </div>
                                <span className={`text-xs leading-tight line-clamp-2 ${
                                  isSelected ? 'text-primary font-bold' : 'text-dark-muted'
                                }`}>
                                  {p.name || 'Untitled'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                      <i className="ri-arrow-left-line text-3xl text-dark-muted/40 mb-2" />
                      <p className="text-sm text-dark font-semibold">Pick a collection to preview</p>
                      <p className="text-xs text-dark-muted mt-1">
                        Click any row on the left to see its products here.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* Summary footer (visible regardless of mode for clarity) */}
      <div className="text-xs text-dark-muted px-1">
        {summaryCount === 0
          ? 'No products selected yet.'
          : allSelected
            ? `Using all ${summaryCount} product${summaryCount === 1 ? '' : 's'}.`
            : `${summaryCount} product${summaryCount === 1 ? '' : 's'} selected.`}
      </div>
    </div>
  );
}
